import { editRequestSurface } from "../staff-feedback";
import {
  PhaseWorkspaceFrame,
  StaffSurfaceState,
  activeAutomationKindForStep,
  materialKindsForStep,
  phaseWorkspaceKind,
  staffActionForStep,
  type StaffWorkState
} from "./index";
import type { StaffPaneProps } from "./console-types";
import type { StaffDetail } from "./model";
import { CallsPane } from "./CallsPane";
import { AnswersPane, ClientMapPane, SubmissionsPane } from "./ConsolePanels";
import { ConfirmationUndoPanel, confirmationUndoTargets } from "./Editors";
import { OverridePanel } from "./OverridePanel";
import { PartnerBriefPanel } from "./PartnerBriefPanel";
import { FeedbackPane } from "./FeedbackPane";
import { PromptRebuildCard } from "./PromptRebuildCard";
import { PaymentsSetupCard } from "./PaymentsSetupCard";
import { MaterialReviewCard } from "./MaterialReview";
import { InstanceCard, MaterialsCard } from "./Operations";
import { AppPhaseCard, BetaPhaseCard, CallPhaseCard, HandoverPhaseCard, PublicationPhaseCard } from "./PhaseCards";
import { TrainingRunCard } from "./TrainingRun";
import { isTrainingRunStale } from "../../shared/training-run";
import { ResourcesPane, StripeConnectCard, resourceKeysForWorkspace } from "./Resources";

/*
 * F138a: the tab dispatcher `DetailPane` and the fifteen-card `Overview` it
 * served are DELETED. They were the whole of the old console: nine tabs behind
 * a drawer, with a second drawer inside the first tab. Six of those tabs drew
 * panels this workspace already draws, filtered to the step the partner is
 * actually on; the three with no other home became the flat sections in
 * src/staff/ClientTools.tsx, and every card of the old Overview is accounted
 * for in the parity table (audit sekce 7).
 *
 * What stays here is the step's own workspace, which was never the problem.
 */

export function PhaseWorkspacePane(props: StaffPaneProps & { step: string }) {
  const kind = phaseWorkspaceKind(props.step);
  let state = phaseWorkspaceState(props.detail, props.step);
  let activeAutomationKind = automationKindForWorkspace(props.detail, props.step);
  // T9: a training run that failed, was stopped, or went silent must not sit
  // under a "Bezi automaticky" chip - the frame chip and the run card would
  // contradict each other (the exact lie fixr5 outlawed). The run is
  // detail-only data, so this is workspace PRESENTATION, never a truth input
  // (fixr5 law); the canonical rail/matrix state is unchanged.
  if (props.step === "training" && state === "automation" && trainingRunNeedsAttention(props.detail)) {
    state = "blocked";
    activeAutomationKind = undefined;
  }
  const body = (() => {
    if (kind === "material") {
      return (
        <div className="staff-review-grid">
          {materialKindsForStep(props.step, props.detail.deliverables).map((materialKind) => (
            <MaterialReviewCard key={materialKind} {...props} kind={materialKind} />
          ))}
        </div>
      );
    }
    // F104: the client-state controls of each step live in OverridePanel,
    // rendered for EVERY workspace below - the per-step ConfirmationUndoPanel
    // copies this pane used to hold are gone so one step never shows the same
    // way back twice.
    if (kind === "profile") {
      return (
        <div className="detail-grid phase-profile-layout">
          <ClientMapPane {...props} />
        </div>
      );
    }
    if (kind === "questionnaire") {
      const questionnaire = props.step === "q2" ? "q2" as const : "q1" as const;
      return (
        <div className="detail-grid">
          <AnswersPane {...props} questionnaire={questionnaire} />
          <SubmissionsPane {...props} questionnaire={questionnaire} />
        </div>
      );
    }
    if (kind === "knowledge") {
      return <div className="detail-grid"><MaterialsCard {...props} /><ResourcesPane {...props} includeKeys={resourceKeysForWorkspace("knowledge")} /></div>;
    }
    // A call step is about THAT call: its stable link, its date, its
    // attendance. It is not the place for know-how uploads or DFY builds.
    if (kind === "call") {
      const callKind = props.step === "onboarding_call" ? "onboarding" : "marketing";
      const hasStepCall = props.detail.calls.some((call) => String(call.kind) === callKind);
      return (
        <div className="detail-grid phase-call-layout">
          <CallPhaseCard {...props} />
          {hasStepCall && <CallsPane {...props} />}
        </div>
      );
    }
    // Training = the instance being built. The live run card (T9) shows the
    // Instance Creator's progress phase by phase; the DFY sales build belongs
    // to the material steps, where the partner reviews its output.
    if (kind === "training") {
      return <div className="detail-grid"><TrainingRunCard {...props} /><InstanceCard {...props} /></div>;
    }
    // Both steps are backed by the `beta` section, so their queue is the
    // podněty about the Buddy - never the notes on a sales material, which
    // belong to that material's own step (audit P-13).
    if (kind === "beta") {
      return <div className="detail-grid"><BetaPhaseCard {...props} /><FeedbackPane {...props} scope="beta" /><PromptRebuildCard {...props} /></div>;
    }
    if (kind === "feedback") return <div className="detail-grid"><FeedbackPane {...props} scope="beta" /><PromptRebuildCard {...props} /></div>;
    if (kind === "stripe") return <StripeConnectCard {...props} />;
    if (kind === "payments") return <div className="detail-grid"><PaymentsSetupCard {...props} /></div>;
    if (kind === "publication") {
      return <div className="detail-grid"><PublicationPhaseCard {...props} /></div>;
    }
    if (kind === "handover") {
      return (
        <div className="detail-grid">
          <HandoverPhaseCard {...props} />
          {/* Pavel's original rescue place keeps the FULL target list: the
              handover is where every earlier confirmation may need a way
              back at once. Per-step control lives in each step (F104). */}
          {state !== "locked" && (
            <ConfirmationUndoPanel
              clientId={props.detail.client.id}
              clientName={props.detail.client.name}
              targets={confirmationUndoTargets(props.detail)}
              t={props.t}
              run={props.run}
            />
          )}
        </div>
      );
    }
    if (kind === "app") return <AppPhaseCard {...props} step={props.step} />;
    // Every rail step has a workspace of its own (WORKSPACE_KINDS); this is
    // the corrupt-payload path, and it says so instead of rendering the whole
    // old console under a heading that names one step (audit P0).
    return (
      <StaffSurfaceState
        body={props.t("staffConsole.workspaceUnknown.body")}
        title={props.t("staffConsole.workspaceUnknown.title")}
        variant="empty"
      />
    );
  })();

  return (
    <PhaseWorkspaceFrame
      state={state}
      step={props.step}
      t={props.t}
      activeAutomationKind={activeAutomationKind}
      automationStats={activeAutomationKind
        ? props.detail.automationStatsByKind?.[activeAutomationKind]
        : undefined}
    >
      {body}
      {/* F106: on a material step the partner briefs THIS material's build and
          can order it made again from scratch. One insertion point, same law
          as OverridePanel below: a step never grows its own copy. */}
      <PartnerBriefPanel
        detail={props.detail}
        step={props.step}
        locale={props.locale}
        t={props.t}
        run={props.run}
      />
      {/* F104: every step carries its own client-state controls (return to
          the client / approve on their behalf) plus the named locked notes.
          One insertion point for all steps - never a per-workspace copy. */}
      <OverridePanel detail={props.detail} step={props.step} t={props.t} run={props.run} />
    </PhaseWorkspaceFrame>
  );
}

/** The live training run says the machine is NOT fine: failed, stopped, or
 * running-but-silent past the heartbeat window (shared/training-run.ts). */
export function trainingRunNeedsAttention(detail: StaffDetail, now = Date.now()): boolean {
  const run = detail.trainingRun;
  if (!run) return false;
  if (run.status === "failed" || run.status === "stopped") return true;
  return (run.status === "running" || run.status === "waiting_input") && isTrainingRunStale(run, now);
}

export function automationKindForWorkspace(detail: StaffDetail, step: string) {
  const editRequest = editRequestSurface(detail.resources, detail.deliverables);
  const salesEditActive = editRequest?.status === "new" && editRequest.kind
    && materialKindsForStep(step, detail.deliverables).includes(editRequest.kind)
    && detail.activeAutomationKinds?.includes("salesedit");
  return activeAutomationKindForStep(
    step,
    detail.activeAutomationKinds ?? [],
    Boolean(salesEditActive)
  );
}

export function phaseWorkspaceState(detail: StaffDetail, step: string): StaffWorkState {
  // One truth: the workspace chip is the canonical step truth from
  // shared/staff-lifecycle.ts, the same value the rail, the hero, the matrix
  // and the queue consume (fixr4). A locked step never blames the client and
  // a step with an open podnět always says the team is on turn.
  return staffActionForStep(detail, step).kind;
}
